import Difficulty from 'app/frontend/types/custom/Difficulty'
import RaidPlan, {
  RaidPlanActor,
  RaidPlanEncounterAssignmentAbility,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import { deleteRequest, getRequest, postRequest, putRequest } from '.'

type RaidPlanOwner =
  | { ownerType: 'team'; ownerId: number }
  | { ownerType: 'user' }

type UserRaidPlansIndex = {
  raidPlans: RaidPlan[]
  teamPlanCounts: Record<number, number>
}

// A partial timeline update. Actors and assignment abilities are identified by
// key so the server can merge them into whatever another editor already saved.
// Those keys travel as values rather than as object keys, because
// ApplicationController#underscore_params! rewrites every incoming param key
// and would mangle a hyphenated actor uuid.
export type RaidPlanTimelinePatch = {
  skeleton?: RaidPlanTimeline
  actor_order?: string[]
  actors?: RaidPlanActor[]
  removed_actor_uuids?: string[]
  abilities?: { key: string; ability: RaidPlanEncounterAssignmentAbility }[]
  removed_ability_keys?: string[]
}

const ownerPath = (owner: RaidPlanOwner) => {
  if (owner.ownerType === 'team')
    return `/api/teams/${owner.ownerId}/raid_plans`
  return '/api/user/raid_plans'
}

const paramsFor = (seasonId?: number, difficulty?: Difficulty) => {
  return new URLSearchParams(
    Object.entries({
      ...(seasonId ? { season_id: seasonId.toString() } : {}),
      ...(difficulty ? { difficulty } : {}),
    }),
  ).toString()
}

const raidPlansUrl = (
  owner: RaidPlanOwner,
  seasonId?: number,
  difficulty?: Difficulty,
) => {
  const params = paramsFor(seasonId, difficulty)
  return `${ownerPath(owner)}${params ? `?${params}` : ''}`
}

export const getRaidPlansIndex = async (
  owner: RaidPlanOwner,
  seasonId?: number,
  difficulty?: Difficulty,
): Promise<UserRaidPlansIndex | RaidPlan[]> => {
  return await getRequest(raidPlansUrl(owner, seasonId, difficulty))
}

export const getRaidPlans = async (
  owner: RaidPlanOwner,
  seasonId?: number,
  difficulty?: Difficulty,
): Promise<RaidPlan[]> => {
  const response = await getRaidPlansIndex(owner, seasonId, difficulty)
  return Array.isArray(response) ? response : response.raidPlans
}

export const getRaidPlan = async (
  owner: RaidPlanOwner,
  planId: number,
): Promise<RaidPlan> => {
  return await getRequest(`${ownerPath(owner)}/${planId}`)
}

export const createRaidPlan = async (
  owner: RaidPlanOwner,
  raidPlan: {
    encounter_id: number
    difficulty: Difficulty
    name: string
    message?: string
    include_message_in_export?: boolean
    timeline?: RaidPlanTimeline
  },
): Promise<RaidPlan> => {
  return await postRequest(ownerPath(owner), {
    raid_plan: raidPlan,
  })
}

export const updateRaidPlan = async (
  owner: RaidPlanOwner,
  planId: number,
  raidPlan: {
    encounter_id?: number
    difficulty?: Difficulty
    name?: string
    message?: string
    include_message_in_export?: boolean
    timeline?: RaidPlanTimeline
    timeline_patch?: RaidPlanTimelinePatch
    client_id?: string
    snapshot?: boolean
  },
): Promise<RaidPlan> => {
  return await putRequest(`${ownerPath(owner)}/${planId}`, {
    raid_plan: raidPlan,
  })
}

export const setDefaultRaidPlan = async (
  teamId: number,
  planId: number,
): Promise<wowaudit.Team> => {
  return await postRequest(
    `/api/teams/${teamId}/raid_plans/${planId}/set_default`,
  )
}

export const deleteRaidPlan = async (
  owner: RaidPlanOwner,
  planId: number,
): Promise<{ success: boolean }> => {
  return await deleteRequest(`${ownerPath(owner)}/${planId}`)
}

export type RaidPlanCastHeatmap = {
  killCount: number
  difficulty: 'mythic' | 'heroic'
  groups: Record<
    string,
    { phases: Record<string, number[]>; playerCount: number }
  >
}

export const getRaidPlanCastHeatmap = async (
  owner: RaidPlanOwner,
  encounterId: number,
  difficulty: Difficulty,
): Promise<RaidPlanCastHeatmap> => {
  return await getRequest(
    `${ownerPath(
      owner,
    )}/cast_heatmap?encounter_id=${encounterId}&difficulty=${difficulty}`,
  )
}

export type RaidPlanDeathSources = {
  events: { id: number; date: string; pullCount: number }[]
  past7Days: number
  past14Days: number
  thisSeason: number
  scopeEvents: wowaudit.EventBasicReference[]
  linkedEventCount: number
  pullCounts: Record<string, number>
}

export type RaidPlanDeathPull = Array<[string, number]>

export type RaidPlanDeaths = {
  pullCount: number
  pulls: RaidPlanDeathPull[]
}

export const getRaidPlanDeathSources = async (
  teamId: number,
  encounterId: number,
  difficulty: Difficulty,
): Promise<RaidPlanDeathSources> => {
  return await getRequest(
    `/api/teams/${teamId}/raid_plans/death_sources?encounter_id=${encounterId}&difficulty=${difficulty}`,
  )
}

export const getRaidPlanDeaths = async (
  teamId: number,
  encounterId: number,
  difficulty: Difficulty,
  source: string,
): Promise<RaidPlanDeaths> => {
  return await getRequest(
    `/api/teams/${teamId}/raid_plans/deaths?encounter_id=${encounterId}&difficulty=${difficulty}&source=${source}`,
  )
}

export const getRaidPlanTopExecutionKills = async (
  owner: RaidPlanOwner,
  encounterId: number,
  difficulty: Difficulty,
): Promise<wowaudit.RaidReportTopExecutionKill[]> => {
  return await getRequest(
    `${ownerPath(
      owner,
    )}/top_execution_kills?encounter_id=${encounterId}&difficulty=${difficulty}`,
  )
}

export const importRaidPlanWclReport = async (
  owner: RaidPlanOwner,
  payload: { reportUrl: string; encounterId: number },
): Promise<{ job_id: string }> => {
  return await postRequest(`${ownerPath(owner)}/import_wcl_report`, payload)
}

export type { RaidPlanOwner, UserRaidPlansIndex }
